import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import NativeHeader from '../components/native/NativeHeader/NativeHeader';
import BottomNavigationBar from '../components/BottomNavigationBar/BottomNavigationBar';
import EmployeeSessionBanner from '../components/EmployeeSessionBanner/EmployeeSessionBanner';
import NetworkBanner from '../components/native/NetworkBanner/NetworkBanner';
import { NativeProListHeaderProvider } from '../contexts/NativeProListHeaderContext';
import { useAndroidBackHandler } from '../hooks/useAndroidBackHandler';
import { useSyncAppIconBadge } from '../hooks/native/useSyncAppIconBadge';
import type { ShellRouteContext } from './shellRouteContext';
import './layoutShell.css';
import './nativeMobileShell.css';
import '../pages/HomeClassic/home-classic-tokens.css';
import '../styles/native-tokens.css';
import '../styles/native-shared.css';

export interface NativeShellProps {
  route: ShellRouteContext;
  children: React.ReactNode;
}

/**
 * Shell Capacitor-only.
 * - NativeHeader (← Logo Compte) remplace la Navbar marketing
 * - Pas de footer marketing
 * - Bottom nav + safe-area iOS/Android
 */
export const NativeMobileShell: React.FC<NativeShellProps> = ({ route, children }) => {
  useAndroidBackHandler();
  useSyncAppIconBadge();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation('native');

  return (
    <NativeProListHeaderProvider>
      <NativeMobileShellInner route={route} location={location} navigate={navigate} t={t}>
        {children}
      </NativeMobileShellInner>
    </NativeProListHeaderProvider>
  );
};

const NativeMobileShellInner: React.FC<
  NativeShellProps & {
    location: ReturnType<typeof useLocation>;
    navigate: ReturnType<typeof useNavigate>;
    t: (key: string) => string;
  }
> = ({ route, children, location, navigate, t }) => {
  const profileTabFromAnimal = (location.state as { profileTab?: 'infos' | 'animaux' | 'favoris' } | null)?.profileTab ?? 'animaux';
  const animalBackToProfile =
    route.pathname.startsWith('/animal') &&
    (location.state as { fromProfile?: boolean } | null)?.fromProfile === true;

  if (route.shouldHideChrome) {
    return (
      <div className="app-shell app-shell--native app-shell--bare">
        <main className="app-shell__content">{children}</main>
      </div>
    );
  }

  const headerTitle = route.nativeHeader.titleKey
    ? t(route.nativeHeader.titleKey)
    : route.nativeHeader.title ?? undefined;

  const isNativeSubPageHeader =
    route.nativeHeader.showBack === true ||
    route.nativeHeader.titleKey != null ||
    route.nativeHeader.title != null;

  const isProListPage = route.isProListPage;

  return (
    <div className="app-shell app-shell--native" data-app-shell="native">
      <div className="app-shell__body">
        {route.showNativeHeader && (
          <NativeHeader
            title={isProListPage ? undefined : headerTitle}
            showLogo={!isProListPage && !headerTitle}
            showBack={isProListPage ? true : route.nativeHeader.showBack ?? undefined}
            backPath={isProListPage ? '/' : animalBackToProfile ? undefined : route.nativeHeader.backPath ?? undefined}
            onBack={
              animalBackToProfile
                ? () => navigate('/profile', { state: { profileTab: profileTabFromAnimal }, replace: true })
                : undefined
            }
            showAccount={!isNativeSubPageHeader && !isProListPage}
            showMessaging={!isNativeSubPageHeader && !isProListPage}
            compact={isProListPage}
          />
        )}
        <NetworkBanner />
        {route.showEmployeeBanner && <EmployeeSessionBanner />}
        <main
          className={`app-shell__content app-shell__content--native${
            route.showBottomNav ? '' : ' app-shell__content--no-tabbar'
          }${!route.showNativeHeader ? ' app-shell__content--native-root' : ''}${
            isProListPage ? ' app-shell__content--pro-list' : ''
          }`}
        >
          {children}
        </main>
      </div>
      {route.showBottomNav && <BottomNavigationBar />}
    </div>
  );
};
